import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { NAV, LENSES } from "../data/content";
import CookieConsent from "./CookieConsent";

const LOGO_URL = "/mee-logo.png";

const Logo = () => (
  <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5 group">
    <img src={LOGO_URL} alt="My Eco Engineer logo" className="h-10 w-10 object-contain transition-transform group-hover:scale-105" />
    <span className="font-display font-medium tracking-tight text-[15px] leading-none">
      My Eco <span className="text-primary">Engineer</span>
    </span>
  </Link>
);

const Header = () => {
  const [open, setOpen] = useState(false);
  const [gpOpen, setGpOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => { setOpen(false); setGpOpen(false); }, [loc.pathname]);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a241a]/90 backdrop-blur-xl border-b border-white/10">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 h-16 flex items-center justify-between">
        <Logo />
        <nav className="hidden lg:flex items-center gap-9">
          <div className="relative" onMouseEnter={() => setGpOpen(true)} onMouseLeave={() => setGpOpen(false)}>
            <NavLink to="/greenpapers" data-testid="nav-greenpapers"
              className="font-mono text-[11px] uppercase tracking-[0.18em] text-foreground/80 hover:text-primary transition-colors flex items-center gap-1">
              Greenpapers <ChevronDown className="h-3 w-3" />
            </NavLink>
            <AnimatePresence>
              {gpOpen && (
                <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 6 }}
                  className="absolute top-full left-0 pt-3 w-64">
                  <div className="bg-[#0a241a] border border-white/10 p-2 shadow-xl">
                    {LENSES.map((l) => (
                      <Link key={l.slug} to={`/greenpapers/category/${l.slug}`}
                        data-testid={`nav-lens-${l.slug}`}
                        className="block px-3 py-2.5 hover:bg-primary/10 transition-colors group">
                        <span className="font-mono text-[10px] text-primary mr-2">{l.index}</span>
                        <span className="text-sm text-foreground/90 group-hover:text-primary">{l.title}</span>
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          {NAV.slice(1).map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `font-mono text-[11px] uppercase tracking-[0.18em] transition-colors flex items-center gap-1.5 ${isActive ? "text-primary" : "text-foreground/80 hover:text-primary"}`}>
              {n.label}
              {n.soon && <span className="font-mono text-[8px] uppercase tracking-widest px-1.5 py-0.5 border border-primary/40 text-primary rounded-full leading-none">Soon</span>}
            </NavLink>
          ))}
        </nav>
        <button data-testid="mobile-menu-toggle" className="lg:hidden text-foreground" onClick={() => setOpen((v) => !v)}>
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-[#0a241a] border-b border-white/10">
            <div className="px-5 py-4 flex flex-col gap-1">
              <Link to="/greenpapers" data-testid="m-nav-greenpapers" className="py-3 font-mono text-xs uppercase tracking-widest border-b border-border">Greenpapers</Link>
              {LENSES.map((l) => (
                <Link key={l.slug} to={`/greenpapers/category/${l.slug}`} className="py-2 pl-4 text-sm text-foreground/70">{l.index} · {l.title}</Link>
              ))}
              {NAV.slice(1).map((n) => (
                <Link key={n.to} to={n.to} data-testid={`m-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`} className="py-3 font-mono text-xs uppercase tracking-widest border-b border-border flex items-center gap-2">
                  {n.label}
                  {n.soon && <span className="font-mono text-[8px] uppercase tracking-widest px-1.5 py-0.5 border border-primary/40 text-primary rounded-full leading-none">Soon</span>}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

const Footer = () => (
  <footer className="bg-[#0a241a] border-t border-white/10">
    <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 grid grid-cols-1 md:grid-cols-3 gap-12">
      <div>
        <Logo />
        <p className="mt-5 text-sm text-muted-foreground max-w-xs leading-relaxed">
          Engineering low-tox living systems. Applied engineering for reducing cumulative exposure and cognitive load.
        </p>
        <div className="flex gap-3 mt-6 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
          <a href="https://www.tiktok.com/@myecoengineer" target="_blank" rel="noopener noreferrer" data-testid="social-tiktok" className="hover:text-primary transition-colors">TikTok</a>
          <span className="text-border">/</span>
          <a href="https://instagram.com/myecoengineer/" target="_blank" rel="noopener noreferrer" data-testid="social-instagram" className="hover:text-primary transition-colors">Instagram</a>
          <span className="text-border">/</span>
          <a href="#" data-testid="social-facebook" className="hover:text-primary transition-colors">Facebook</a>
        </div>
      </div>
      <div>
        <p className="mono-label mb-5">Navigation</p>
        <ul className="space-y-3 text-sm">
          <li><Link to="/greenpapers" className="text-foreground/80 hover:text-primary transition-colors">Greenpapers</Link></li>
          <li><Link to="/products" className="text-foreground/80 hover:text-primary transition-colors">Products</Link></li>
          <li><Link to="/services" className="text-foreground/80 hover:text-primary transition-colors">Services</Link></li>
          <li><Link to="/marketplace" className="text-foreground/80 hover:text-primary transition-colors">Clean Products</Link></li>
          <li><Link to="/about" className="text-foreground/80 hover:text-primary transition-colors">About</Link></li>
          <li><Link to="/contact" className="text-foreground/80 hover:text-primary transition-colors">Contact</Link></li>
        </ul>
      </div>
      <div>
        <p className="mono-label mb-5">Legal</p>
        <ul className="space-y-3 text-sm">
          <li><Link to="/legal/terms" className="text-foreground/80 hover:text-primary transition-colors">Terms of Use</Link></li>
          <li><Link to="/legal/privacy" className="text-foreground/80 hover:text-primary transition-colors">Privacy & Cookie Policy</Link></li>
          <li><button data-testid="footer-cookie-prefs" onClick={() => window.dispatchEvent(new CustomEvent("open-cookie-prefs"))} className="text-foreground/80 hover:text-primary transition-colors">Cookie Preferences</button></li>
        </ul>
      </div>
    </div>
    <div className="border-t border-border">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-5 flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between text-xs text-muted-foreground font-mono">
        <span data-testid="footer-copyright">© 2026 My Eco Engineer. Operated by Colimon Systems Group, LLC. All rights reserved.</span>
        <span data-testid="footer-legal-entity" className="text-muted-foreground/80">My Eco Engineer is operated by Colimon Systems Group, LLC, a Virginia limited liability company.</span>
      </div>
    </div>
  </footer>
);

export default function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-base">
      <Header />
      <main className="flex-1 pt-16 home-white">{children}</main>
      <Footer />
      <CookieConsent />
    </div>
  );
}
